import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {requestContext} from '../../../../../../common/requestContext';
import {ViewerSettingFontType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingFontItemType.interface';
import {ViewerSettingThemeType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingThemeItemType.interface';
import {PagewebError} from '../../../../../../errors/PagewebError.class';
import {
  CONTENT_MARGIN_DEFAULT,
  FONT_FAMILY_DEFAULT,
  FONT_SIZE_DEFAULT,
  IS_TWO_PAGE_LAYOUT_DEFAULT,
  LINE_GAP_DEFAULT,
  TEXT_VIEWER_STATE_NAME,
  THEME_DEFAULT,
} from '../constant/viewerConstant';
import {ContentMetaInfo} from '../type/ContentMetaInfo.interface';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {getChapterAndPage} from '../util/getChapterAndPage';
import {TextViewerUrls} from './TextViewerUrls.interface';
import {TextViewerViewData} from './TextViewerViewData.interface';
import {TextViewerState} from './textViewerState.interface';

export const TEXT_VIEWER_INITIAL_STATE = {
  settings: {
    readType: TextViewerReadType.Page,
    theme: THEME_DEFAULT,
    fontFamily: FONT_FAMILY_DEFAULT,
    fontSize: FONT_SIZE_DEFAULT,
    lineGap: LINE_GAP_DEFAULT,
    contentMargin: CONTENT_MARGIN_DEFAULT,
    isTwoPageLayout: IS_TWO_PAGE_LAYOUT_DEFAULT,
  },
  viewData: {},
} as TextViewerState;
export const textViewerSlice = createSlice({
  name: TEXT_VIEWER_STATE_NAME,
  initialState: TEXT_VIEWER_INITIAL_STATE,
  reducers: {
    changeReadType: (state, action: PayloadAction<TextViewerReadType>) => {
      state.settings.readType = action.payload;
    },
    changeTheme: (state, action: PayloadAction<ViewerSettingThemeType>) => {
      state.settings.theme = action.payload;
    },
    changeFontFamily: (state, action: PayloadAction<ViewerSettingFontType>) => {
      state.settings.fontFamily = action.payload;
    },
    changeFontSize: (state, action: PayloadAction<number>) => {
      state.settings.fontSize = action.payload;
    },
    decreaseFontSize: state => {
      state.settings.fontSize -= 1;
    },
    increaseFontSize: state => {
      state.settings.fontSize += 1;
    },
    changeLineGap: (state, action: PayloadAction<number>) => {
      state.settings.lineGap = action.payload;
    },
    decreaseLineGap: state => {
      state.settings.lineGap -= 1;
    },
    increaseLineGap: state => {
      state.settings.lineGap += 1;
    },
    changeContentMargin: (state, action: PayloadAction<number>) => {
      state.settings.contentMargin = action.payload;
    },
    decreaseContentMargin: state => {
      state.settings.contentMargin -= 1;
    },
    increaseContentMargin: state => {
      state.settings.contentMargin += 1;
    },
    resetContentOptions: state => {
      state.settings.theme = THEME_DEFAULT;
      state.settings.fontFamily = FONT_FAMILY_DEFAULT;
      state.settings.fontSize = FONT_SIZE_DEFAULT;
      state.settings.lineGap = LINE_GAP_DEFAULT;
      state.settings.contentMargin = CONTENT_MARGIN_DEFAULT;
    },
    changeTwoPageLayout: (state, action: PayloadAction<boolean>) => {
      state.settings.isTwoPageLayout = action.payload;
    },
    toggleTwoPageLayout: state => {
      state.settings.isTwoPageLayout = !state.settings.isTwoPageLayout;
    },

    setUrls: (
      state,
      action: PayloadAction<{
        productId: number;
        urls: TextViewerUrls;
      }>
    ) => {
      const {productId, urls} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.urls = urls;
      state.viewData[productId] = productViewData;
    },
    setMetaInfo: (
      state,
      action: PayloadAction<{
        productId: number;
        metaInfo: ContentMetaInfo;
      }>
    ) => {
      const {productId, metaInfo} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.metaInfo = metaInfo;
      state.viewData[productId] = productViewData;
    },
    setChapterCount: (
      state,
      action: PayloadAction<{
        productId: number;
        chapterCount: number;
      }>
    ) => {
      const {productId, chapterCount} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.chapterCount = chapterCount;
      state.viewData[productId] = productViewData;
    },
    setPreloadedHtmlInfo: (
      state,
      action: PayloadAction<{
        productId: number;
        chapterId: number;
        htmlInfo: HtmlInfo;
      }>
    ) => {
      const {productId, chapterId, htmlInfo} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const preloadedHtmlInfoList = productViewData.preloadedHtmlInfoList || [];
      preloadedHtmlInfoList[chapterId] = htmlInfo;
      productViewData.preloadedHtmlInfoList = preloadedHtmlInfoList;
      state.viewData[productId] = productViewData;
    },
    setChapterHtmlInfo: (
      state,
      action: PayloadAction<{
        productId: number;
        chapterId: number;
        htmlInfo: HtmlInfo;
      }>
    ) => {
      const {productId, chapterId, htmlInfo} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const htmlInfoList = productViewData.htmlInfoList || [];
      htmlInfoList[chapterId] = htmlInfo;
      productViewData.htmlInfoList = htmlInfoList;
      state.viewData[productId] = productViewData;
    },
    setIsDataLoaded: (
      state,
      action: PayloadAction<{
        productId: number;
        loaded: boolean;
      }>
    ) => {
      const {productId, loaded} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isDataLoaded = loaded;
      state.viewData[productId] = productViewData;
    },
    setIsContentLoaded: (
      state,
      action: PayloadAction<{
        productId: number;
        loaded: boolean;
      }>
    ) => {
      const {productId, loaded} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isContentLoaded = loaded;
      state.viewData[productId] = productViewData;
    },
    setCurrentChapterId: (
      state,
      action: PayloadAction<{
        productId: number;
        chapterId: number;
        setIsDrawing?: boolean;
      }>
    ) => {
      const {productId, chapterId, setIsDrawing = true} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      // 첫 화 보기에서는 챕터 변경 시에도 그리기를 하지 않습니다.
      if (productViewData.currentChapterId !== chapterId && setIsDrawing) {
        productViewData.isDrawing = true;
      }
      productViewData.currentChapterId = chapterId;
      state.viewData[productId] = productViewData;
    },
    setCurrentPage: (
      state,
      action: PayloadAction<{
        productId: number;
        page: number;
      }>
    ) => {
      const {productId, page} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const isTwoPageLayout =
        requestContext.requestInfo.display.isMobile ||
        state.settings.readType === TextViewerReadType.Scroll
          ? false
          : state.settings.isTwoPageLayout;
      let currentPage = page > 0 ? page : 1;
      if (isTwoPageLayout) {
        // 두쪽보기 상태에서는 왼쪽 페이지를 기준으로 동작합니다.
        currentPage = currentPage - (currentPage % 2 === 0 ? 1 : 0);
      }
      productViewData.currentPage = currentPage;
      state.viewData[productId] = productViewData;
    },
    setCurrentRate: (
      state,
      action: PayloadAction<{
        productId: number;
        rate: number;
      }>
    ) => {
      const {productId, rate} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.currentRate = rate;
      state.viewData[productId] = productViewData;
    },
    setCurrentHtmlInfo: (
      state,
      action: PayloadAction<{
        productId: number;
        htmlInfo: HtmlInfo;
      }>
    ) => {
      const {productId, htmlInfo} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.currentHtmlInfo = htmlInfo;
      state.viewData[productId] = productViewData;
    },
    setCurrentPageCount: (
      state,
      action: PayloadAction<{
        productId: number;
        pageCount: number;
      }>
    ) => {
      const {productId, pageCount} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.currentPageCount = pageCount;
      state.viewData[productId] = productViewData;
    },
    clearPageCountList: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.chapterPageCountList = [];
      state.viewData[productId] = productViewData;
    },
    setChapterPageCount: (
      state,
      action: PayloadAction<{
        productId: number;
        chapterId: number;
        pageCount: number;
      }>
    ) => {
      const {productId, chapterId, pageCount} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const chapterPageCountList = productViewData.chapterPageCountList || [];
      chapterPageCountList[chapterId] = pageCount;
      productViewData.chapterPageCountList = chapterPageCountList;
      state.viewData[productId] = productViewData;
    },
    setChapterPageCountList: (
      state,
      action: PayloadAction<{
        productId: number;
        pageCountList: {
          chapterId: number;
          pageCount: number;
        }[];
      }>
    ) => {
      const {productId, pageCountList} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const chapterPageCountList = productViewData.chapterPageCountList || [];
      pageCountList.forEach(({chapterId, pageCount}) => {
        chapterPageCountList[chapterId] = pageCount;
      });
      productViewData.chapterPageCountList = chapterPageCountList;
      state.viewData[productId] = productViewData;
    },
    setIsViewerEnd: (
      state,
      action: PayloadAction<{
        productId: number;
        isViewerEnd: boolean;
      }>
    ) => {
      const {productId, isViewerEnd} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isViewerEnd = isViewerEnd;
      state.viewData[productId] = productViewData;
    },
    goToPrevPage: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };

      const isTwoPageLayout =
        requestContext.requestInfo.display.isMobile ||
        state.settings.readType === TextViewerReadType.Scroll
          ? false
          : state.settings.isTwoPageLayout;
      let currentPage = productViewData.currentPage;
      if (isTwoPageLayout) {
        // 두쪽보기 상태에서는 왼쪽 페이지를 기준으로 동작합니다.
        currentPage = currentPage - (currentPage % 2 === 0 ? 1 : 0);
      }
      let prevChapterId;
      let prevPageCount;
      let prevPage = currentPage - (isTwoPageLayout ? 2 : 1);
      if (prevPage < 1) {
        const chapterPageCountList = productViewData.chapterPageCountList;
        if (!chapterPageCountList) {
          return;
        }
        prevChapterId = getPrevChapterId(state, productId);

        if (prevChapterId < 0) {
          return;
        }
        prevPageCount = chapterPageCountList[prevChapterId] ?? 0;
        prevPage = prevPageCount;
        if (isTwoPageLayout) {
          prevPage = prevPage - (prevPage % 2 === 0 ? 1 : 0);
        }
      }

      if (prevChapterId !== undefined) {
        productViewData.isDrawing = true;
        productViewData.currentChapterId = prevChapterId;
        productViewData.currentPageCount = prevPageCount;
      }
      productViewData.currentPage = prevPage;

      state.viewData[productId] = productViewData;
    },
    goToNextPage: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };

      const chapterPageCountList = productViewData.chapterPageCountList;
      if (!chapterPageCountList) {
        return;
      }
      const isTwoPageLayout =
        requestContext.requestInfo.display.isMobile ||
        state.settings.readType === TextViewerReadType.Scroll
          ? false
          : state.settings.isTwoPageLayout;
      let currentPage = productViewData.currentPage;
      if (isTwoPageLayout) {
        // 두쪽보기 상태에서는 왼쪽 페이지를 기준으로 동작합니다.
        currentPage = currentPage - (currentPage % 2 === 0 ? 1 : 0);
      }
      const currentChapterId = productViewData.currentChapterId;
      const currentPageCount = chapterPageCountList[currentChapterId] ?? 0;
      let nextChapterId;
      let nextPageCount;
      let nextPage = currentPage + (isTwoPageLayout ? 2 : 1);
      if (nextPage > currentPageCount) {
        nextChapterId = getNextChapterId(state, productId);

        if (nextChapterId > chapterPageCountList.length - 1) {
          // 뷰어엔드는 currentChapterId를 챕터 수 이상으로 넘겨
          // TextViewerProgressContainer에서 처리하도록 합니다.
          productViewData.currentChapterId = chapterPageCountList.length;
          productViewData.currentPage = 1;
          state.viewData[productId] = productViewData;
          return;
        }
        nextPageCount = chapterPageCountList[nextChapterId];
        nextPage = 1;
      }

      if (nextChapterId !== undefined) {
        productViewData.isDrawing = true;
        productViewData.currentChapterId = nextChapterId;
        productViewData.currentPageCount = nextPageCount;
      }
      productViewData.currentPage = nextPage;

      state.viewData[productId] = productViewData;
    },
    goToPrevChapter: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };

      const prevChapterId = getPrevChapterId(state, productId);

      if (prevChapterId < 0) {
        return;
      }
      const prevHtmlInfo = productViewData.htmlInfoList?.[prevChapterId];
      if (!prevHtmlInfo) {
        return;
      }
      productViewData.isDrawing = true;
      productViewData.currentChapterId = prevChapterId;
      productViewData.chapterChangeDirection = 'up';
      state.viewData[productId] = productViewData;
    },
    goToNextChapter: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      const nextChapterId = getNextChapterId(state, productId);

      const chapterCount = productViewData.chapterCount;
      if (!chapterCount) {
        return;
      }
      if (nextChapterId > chapterCount) {
        return;
      }

      productViewData.isDrawing = true;
      productViewData.currentChapterId = nextChapterId;
      productViewData.chapterChangeDirection = 'down';
      state.viewData[productId] = productViewData;
    },
    goToSubTotalPage: (
      state,
      action: PayloadAction<{
        productId: number;
        subTotalPage: number;
      }>
    ) => {
      const {productId, subTotalPage} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };

      const chapterPageCountList = productViewData.chapterPageCountList;
      const isTwoPageLayout =
        requestContext.requestInfo.display.isMobile ||
        state.settings.readType === TextViewerReadType.Scroll
          ? false
          : state.settings.isTwoPageLayout;
      const {chapterId: newChapterId, page} = getChapterAndPage({
        subTotalPage,
        chapterPageCountList,
      });

      if (newChapterId === undefined) {
        return;
      }

      let newPage = page;
      if (isTwoPageLayout) {
        // 두쪽보기 상태에서는 왼쪽 페이지를 기준으로 동작합니다.
        newPage = newPage - (newPage % 2 === 0 ? 1 : 0);
      }

      if (productViewData.currentChapterId !== newChapterId) {
        productViewData.isDrawing = true;
      }
      productViewData.currentChapterId = newChapterId;
      productViewData.currentPage = newPage;
      productViewData.currentPageCount = chapterPageCountList
        ? chapterPageCountList[newChapterId]
        : 0;

      state.viewData[productId] = productViewData;
    },
    setIsProgressBarExpanded: (
      state,
      action: PayloadAction<{
        productId: number;
        isProgressBarExpanded: boolean;
      }>
    ) => {
      const {productId, isProgressBarExpanded} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isProgressBarExpanded = isProgressBarExpanded;
      state.viewData[productId] = productViewData;
    },
    setLoadedProgress: (
      state,
      action: PayloadAction<{
        productId: number;
        progress: number;
      }>
    ) => {
      const {productId, progress} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.loadedProgress = progress;
      state.viewData[productId] = productViewData;
    },
    setCurrentProgress: (
      state,
      action: PayloadAction<{
        productId: number;
        progress: number;
      }>
    ) => {
      const {productId, progress} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.currentProgress = progress;
      state.viewData[productId] = productViewData;
    },
    setIsHandlingSeekBar: (
      state,
      action: PayloadAction<{
        productId: number;
        isHandling: boolean;
      }>
    ) => {
      const {productId, isHandling} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isHandlingSeekBar = isHandling;
      state.viewData[productId] = productViewData;
    },
    clearCurrentProgress: (
      state,
      action: PayloadAction<{productId: number}>
    ) => {
      const {productId} = action.payload;
      const productViewData = {
        ...state.viewData[productId],
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.currentHtmlInfo = undefined;
      state.viewData[productId] = productViewData;
    },
    setIsDone: (
      state,
      action: PayloadAction<{productId: number; isDone: boolean}>
    ) => {
      const {productId, isDone} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isDone = isDone;
      state.viewData[productId] = productViewData;
    },
    clearChapterChangeDirection: (
      state,
      action: PayloadAction<{
        productId: number;
      }>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.chapterChangeDirection = undefined;
      state.viewData[productId] = productViewData;
    },
    setIsDrawing: (
      state,
      action: PayloadAction<{productId: number; isDrawing: boolean}>
    ) => {
      const {productId, isDrawing} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isDrawing = isDrawing;
      state.viewData[productId] = productViewData;
    },
    setIsPageCalculating: (
      state,
      action: PayloadAction<{productId: number; isPageCalculating: boolean}>
    ) => {
      const {productId, isPageCalculating} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isPageCalculating = isPageCalculating;
      state.viewData[productId] = productViewData;
    },
    setTextViewerError: (
      state,
      action: PayloadAction<{productId: number; error: PagewebError}>
    ) => {
      const {productId, error} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.error = error;
      state.viewData[productId] = productViewData;
    },
    clearTextViewerStatus: (
      state,
      action: PayloadAction<{productId: number}>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.isContentLoaded = false;
      productViewData.isDrawing = true;
      productViewData.isPageCalculating = true;
      state.viewData[productId] = productViewData;
    },
    clearTextViewerError: (
      state,
      action: PayloadAction<{productId: number}>
    ) => {
      const {productId} = action.payload;
      const productViewData = state.viewData[productId] || {
        ...INITIAL_TEXT_VIEWER_VIEW_DATA,
      };
      productViewData.error = undefined;
      state.viewData[productId] = productViewData;
    },
  },
});

const getPrevChapterId = (
  state: TextViewerState,
  productId: number
): number => {
  const productViewData = state.viewData[productId] || {
    ...INITIAL_TEXT_VIEWER_VIEW_DATA,
  };
  const chapterPageCountList = productViewData.chapterPageCountList || [];
  const currentChapterId = productViewData.currentChapterId;
  let prevChapterId;
  // 이전 챕터가 빈 챕터일 수 있으니 내용 있는 챕터까지 슬라이딩
  for (
    prevChapterId = currentChapterId - 1;
    prevChapterId >= 0 && !chapterPageCountList[prevChapterId];
  ) {
    prevChapterId--;
  }

  return prevChapterId;
};

const getNextChapterId = (
  state: TextViewerState,
  productId: number
): number => {
  const productViewData = state.viewData[productId] || {
    ...INITIAL_TEXT_VIEWER_VIEW_DATA,
  };
  const chapterPageCountList = productViewData.chapterPageCountList || [];
  const currentChapterId = productViewData.currentChapterId;
  let nextChapterId;
  // 다음 챕터가 빈 챕터일 수 있으니 내용 있는 챕터까지 슬라이딩
  for (
    nextChapterId = currentChapterId + 1;
    nextChapterId < chapterPageCountList.length &&
    !chapterPageCountList[nextChapterId];
  ) {
    nextChapterId++;
  }

  return nextChapterId;
};

export const {
  changeReadType,
  changeTheme,
  changeFontFamily,
  changeFontSize,
  decreaseFontSize,
  increaseFontSize,
  changeLineGap,
  decreaseLineGap,
  increaseLineGap,
  changeContentMargin,
  decreaseContentMargin,
  increaseContentMargin,
  resetContentOptions,
  changeTwoPageLayout,
  toggleTwoPageLayout,
  setUrls,
  setMetaInfo,
  setChapterCount,
  setChapterPageCountList,
  setPreloadedHtmlInfo,
  setChapterHtmlInfo,
  setIsDataLoaded,
  setIsContentLoaded,
  setCurrentChapterId,
  setCurrentPage,
  setCurrentRate,
  setCurrentHtmlInfo,
  setCurrentPageCount,
  clearPageCountList,
  setChapterPageCount,
  setIsViewerEnd,
  goToPrevPage,
  goToNextPage,
  goToPrevChapter,
  goToNextChapter,
  goToSubTotalPage,

  setTextViewerError,
  setIsProgressBarExpanded,
  setLoadedProgress,
  setCurrentProgress,
  setIsHandlingSeekBar,
  clearCurrentProgress,
  clearChapterChangeDirection,
  setIsDone,
  setIsDrawing,
  setIsPageCalculating,
  clearTextViewerStatus,
  clearTextViewerError,
} = textViewerSlice.actions;

const INITIAL_TEXT_VIEWER_VIEW_DATA: TextViewerViewData = {
  isDataLoaded: false,
  isContentLoaded: false,
  currentChapterId: 0,
  currentPage: 1,
  currentRate: 0,
  isProgressBarExpanded: false,
  loadedProgress: 0,
  currentProgress: 0,
  isHandlingSeekBar: false,
  isDone: false,
  isDrawing: true,
  isPageCalculating: true,
  error: undefined,
};
